import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase, type Profile } from '../lib/supabase';
import { Session } from '@supabase/supabase-js';
import { log, warn, error as logError } from '../utils/logger';

interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  username?: string;
  profileColor?: string;
  isSubscribed: boolean;
  subscriptionTier: 'free' | 'elite';
  joinedAt: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  session: Session | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signup: (userData: SignupData) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  updateUserProfile: (data: Partial<AuthUser>) => Promise<boolean>;
}

interface SignupData {
  name?: string;
  username?: string;
  email: string;
  password: string;
  paymentIntentId?: string;
  subscriptionTier?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loginInProgress, setLoginInProgress] = useState(false);

  // Initialize auth state and listen for changes
  useEffect(() => {
    let mounted = true;

    // Get initial session (simplified)
    const getInitialSession = async () => {
      try {
        log('🔍 Getting initial session...');
        
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (mounted) {
          if (error) {
            logError('❌ Error getting session:', error);
            setSession(null);
            setIsLoading(false);
          } else {
            log('✅ Session retrieved:', session ? `User logged in: ${session.user.email}` : 'No session');
            setSession(session);
            log('✅ Auth initialization complete');
            setIsLoading(false);
            
            if (session?.user) {
              log('👤 Loading user profile...');
              // Load profile asynchronously without blocking auth completion
              loadUserProfile(session.user.id).catch(err => {
                if (process.env.NODE_ENV === 'development') {
                  warn('Profile loading failed, continuing with basic auth:', err);
                }
              });
            }
          }
        }
      } catch (error) {
        logError('❌ Error in getInitialSession:', error);
        if (mounted) {
          warn('⚠️ Auth error - proceeding without authentication');
          setSession(null);
          setIsLoading(false);
        }
      }
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;

        if (process.env.NODE_ENV === 'development') {
          console.log('🔄 Auth state changed:', event, 'Login in progress:', loginInProgress);
        }
        
        // Only process auth state changes that aren't from failed login attempts
        if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || !loginInProgress) {
          setSession(session);
          setIsLoading(false);
          
          if (session?.user) {
            // Clear login in progress flag on successful login
            if (event === 'SIGNED_IN') {
              setLoginInProgress(false);
            }
            // Load profile asynchronously without blocking
            loadUserProfile(session.user.id).catch(err => {
              if (process.env.NODE_ENV === 'development') {
                warn('Profile loading failed in auth change:', err);
              }
            });
          } else if (!loginInProgress) {
            // Only clear user if we're not in the middle of a login attempt
            setUser(null);
          }
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const loadUserProfile = async (userId: string) => {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        // Still set user with basic info from auth session if profile doesn't exist
        setUser({
          id: userId,
          email: '',
          firstName: 'User',
          lastName: '',
          isSubscribed: false,
          subscriptionTier: 'free',
          joinedAt: new Date().toISOString()
        });
        return;
      }

      if (profile) {
        const authUser: AuthUser = {
          id: profile.id,
          email: profile.email,
          firstName: profile.first_name || '',
          lastName: profile.last_name || '',
          username: profile.username || undefined,
          profileColor: profile.profile_color || '#3B82F6',
          isSubscribed: profile.is_subscribed,
          subscriptionTier: profile.subscription_tier,
          joinedAt: profile.created_at
        };
        setUser(authUser);
      }
    } catch (error) {
      logError('Error loading user profile:', error);
      // Fallback to basic user data
      setUser({
        id: userId,
        email: '',
        firstName: 'User',
        lastName: '',
        isSubscribed: false,
        subscriptionTier: 'free',
        joinedAt: new Date().toISOString()
      });
    }
  };

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setLoginInProgress(true);
      
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        setLoginInProgress(false); // Clear flag on failure
        return { success: false, error: error.message };
      }

      // Don't clear loginInProgress here - let the auth state change handle it
      return { success: true };
    } catch (error) {
      setLoginInProgress(false); // Clear flag on exception
      logError('Login error:', error);
      return { success: false, error: 'An unexpected error occurred' };
    }
  };

  const signup = async (userData: SignupData): Promise<{ success: boolean; error?: string }> => {
    try {
      setIsLoading(true);
      
      const { error } = await supabase.auth.signUp({
        email: userData.email,
        password: userData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/login?confirmed=true`,
          data: {
            name: userData.name,
            username: userData.username
          }
        }
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      logError('Signup error:', error);
      return { success: false, error: 'An unexpected error occurred' };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        logError('Logout error:', error);
      }
    } catch (error) {
      logError('Logout error:', error);
    }
  };

  const updateUserProfile = async (data: Partial<AuthUser>): Promise<boolean> => {
    try {
      if (!user) return false;
      
      setIsLoading(true);
      
      // First, try to update basic fields
      const basicUpdateData: Partial<Profile> = {};
      if (data.firstName !== undefined) basicUpdateData.first_name = data.firstName;
      if (data.lastName !== undefined) basicUpdateData.last_name = data.lastName;
      if (data.username !== undefined) basicUpdateData.username = data.username;
      if (data.subscriptionTier !== undefined) basicUpdateData.subscription_tier = data.subscriptionTier;
      if (data.isSubscribed !== undefined) basicUpdateData.is_subscribed = data.isSubscribed;
      
      if (Object.keys(basicUpdateData).length > 0) {
        const { error: basicError } = await supabase
          .from('profiles')
          .update(basicUpdateData)
          .eq('id', user.id);
        
        if (basicError) {
          logError('Basic profile update error:', basicError);
          return false;
        }
      }
      
      // Try to update profile_color separately (in case column doesn't exist yet)
      if (data.profileColor !== undefined) {
        const { error: colorError } = await supabase
          .from('profiles')
          .update({ profile_color: data.profileColor })
          .eq('id', user.id);
        
        if (colorError) {
          console.warn('Profile color update failed (column may not exist):', colorError);
          // Don't fail the whole update just because color update failed
        }
      }
      
      // Reload user profile
      await loadUserProfile(user.id);
      
      return true;
    } catch (error) {
      logError('Profile update error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    session,
    isLoading,
    isAuthenticated: !!session?.user,
    login,
    signup,
    logout,
    updateUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;